import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import type { MediaObject, MediaVersion } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';
import { PageShell } from '../components/layout/PageShell';
import { usePublishViewSnapshot } from '../components/shogun/usePublishViewSnapshot';
import {
  MessageDiagramView,
  ensureStandaloneSvg,
  type MessageDiagramViewState,
} from '../components/chat/MessageDiagram';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
const UPLOAD_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.svg,.webp,.html,application/pdf,image/png,image/jpeg,image/svg+xml,image/webp,text/html';

const LIST_PAGE = 200;

async function listAllMedia(trashed: boolean): Promise<MediaObject[]> {
  const sdk = getSDK();
  const items: MediaObject[] = [];
  let cursor: string | undefined;
  for (;;) {
    const page = await sdk.media.list(
      cursor === undefined ? { trashed, limit: LIST_PAGE } : { trashed, limit: LIST_PAGE, cursor },
    );
    items.push(...page.items);
    if (page.next_cursor === undefined || page.next_cursor === '') {
      return items;
    }
    cursor = page.next_cursor;
  }
}

const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  html: 'text/html',
};

const ALLOWED_MIME = new Set(Object.values(EXT_TO_MIME));

const MIME_FOR_FORMAT: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  pdf: 'application/pdf',
  html: 'text/html',
};

export type ArtifactPreviewState =
  | MessageDiagramViewState
  | {
      kind: 'ready-unmeasured';
      previewUrl: string;
      previewKind: 'svg' | 'png';
      warnings: string[];
      label: string;
    }
  | { kind: 'download-only'; label: string; warnings: string[] };

export interface ArtifactRowModel {
  id: string;
  label: string;
  kind: 'diagram' | 'upload';
  current_version: number;
  created_at: string;
  session_id?: string;
  project_id?: string;
  deleted_at?: string;
}

export interface ArtifactVersionRow {
  version: number;
  created_at: string;
  warnings: string[];
  total_bytes: number;
}

export interface ArtifactsLibraryViewProps {
  trashed: boolean;
  items: ArtifactRowModel[];
  selectedIds: string[];
  loading: boolean;
  error?: string;
  uploadError?: string;
  uploading?: boolean;
  bulkBusy?: boolean;
  writeBusy?: boolean;
  onToggleTrashed: (trashed: boolean) => void;
  onOpen: (id: string) => void;
  onUpload: (file: File) => void;
  onToggleSelected: (id: string) => void;
  onSelectAll: () => void;
  onClearSelected: () => void;
  onBulkTrash: () => void;
  onBulkRestore: () => void;
  onBulkEmpty: () => void;
  onMoveToTrash: (id: string) => void;
  onRestore: (id: string) => void;
  onPurge: (id: string) => void;
  onEmptyTrash: () => void;
}

export interface ArtifactsDetailViewProps {
  item: ArtifactRowModel;
  versions: ArtifactVersionRow[];
  createdAtLabel: string;
  sessionHref?: string;
  projectHref?: string;
  projectError?: string;
  preview: ArtifactPreviewState;
  downloadError?: string;
  actionError?: string;
  onBack: () => void;
  onMoveToTrash?: () => void;
  onRestoreFromTrash?: () => void;
  onPurge?: () => void;
  onDownload: () => void;
  onRestoreVersion?: (version: number) => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function classifyUpload(file: File): { mime: string } {
  const typed = file.type !== '' && ALLOWED_MIME.has(file.type) ? file.type : undefined;
  const ext = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
  const fromExt = ext ? EXT_TO_MIME[ext] : undefined;
  const mime = typed ?? fromExt;
  if (!mime) {
    throw new Error(
      `unsupported file type ${file.type || file.name}: allowed PDF PNG JPEG SVG WebP HTML`,
    );
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    throw new Error(`file exceeds 25 MB cap (${file.size} bytes)`);
  }
  return { mime };
}

function rowFromObject(object: MediaObject): ArtifactRowModel {
  return {
    id: object.id,
    label: object.label,
    kind: object.kind,
    current_version: object.current_version,
    created_at: object.created_at,
    session_id: object.session_id,
    project_id: object.project_id,
    deleted_at: object.deleted_at,
  };
}

function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`artifact created_at is not a date: ${iso}`);
  }
  return date.toLocaleString();
}

function filenameFor(label: string, suffix: string): string {
  const stem = label.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  if (stem.length === 0) {
    throw new Error('artifact label produced an empty download name');
  }
  return `${stem}-${suffix}`;
}

function triggerDownload(bytes: ArrayBuffer, filename: string, mime: string): void {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function currentVersion(object: MediaObject): MediaVersion {
  const versions = object.versions;
  if (!versions || versions.length === 0) {
    throw new Error(`media ${object.id} has no versions`);
  }
  const hit = versions.find((row) => row.version === object.current_version);
  if (!hit) {
    throw new Error(`media ${object.id} missing version ${object.current_version}`);
  }
  return hit;
}

function previewMime(format: string): string {
  const mime = MIME_FOR_FORMAT[format];
  if (!mime) {
    throw new Error(`no preview mime for format ${format}`);
  }
  return mime;
}

export function ArtifactPreview({
  state,
  downloadError,
  onDownload,
}: {
  state: ArtifactPreviewState;
  downloadError?: string;
  onDownload?: () => void;
}) {
  if (state.kind === 'ready-unmeasured') {
    return (
      <figure
        data-testid="artifact-preview"
        data-preview-state="ready"
        data-has-warnings={state.warnings.length > 0 ? 'true' : 'false'}
        data-preview-kind={state.previewKind}
        className="mt-3 max-w-full overflow-hidden rounded-md bg-card"
      >
        {state.warnings.length > 0 ? (
          <div
            data-testid="artifact-preview-warning"
            className="px-3 py-2 text-xs text-muted-foreground"
          >
            This drawing contains interactivity that will not run here: {state.warnings.join(', ')}.
            Download the file to open it elsewhere.
          </div>
        ) : null}
        <img
          data-testid="artifact-preview-image"
          src={state.previewUrl}
          alt={state.label}
          className="block max-w-full bg-transparent"
          style={{ filter: 'none' }}
        />
        <figcaption className="flex flex-wrap items-center gap-2 px-3 py-2">
          {onDownload ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="artifact-preview-download"
              onClick={onDownload}
            >
              Download
            </Button>
          ) : null}
          {downloadError ? (
            <span data-testid="artifact-preview-download-error" className="text-xs text-destructive">
              {downloadError}
            </span>
          ) : null}
        </figcaption>
      </figure>
    );
  }

  if (state.kind !== 'download-only') {
    return (
      <div data-testid="artifact-preview" data-preview-state={state.kind}>
        <MessageDiagramView
          state={state}
          onDownloadLatest={onDownload}
          downloadError={downloadError}
        />
      </div>
    );
  }

  return (
    <figure
      data-testid="artifact-preview"
      data-preview-state="download-only"
      data-has-warnings={state.warnings.length > 0 ? 'true' : 'false'}
      className="mt-3 max-w-full overflow-hidden rounded-md bg-card"
    >
      {state.warnings.length > 0 ? (
        <div
          data-testid="artifact-preview-warning"
          className="px-3 py-2 text-xs text-muted-foreground"
        >
          This drawing contains interactivity that will not run here: {state.warnings.join(', ')}.
          Download the file to open it elsewhere.
        </div>
      ) : null}
      <div
        data-testid="artifact-preview-download-only"
        className="px-3 py-4 text-sm text-muted-foreground"
      >
        This file cannot be previewed here without executing it. Download the file.
      </div>
      <figcaption className="flex flex-wrap items-center gap-2 px-3 py-2">
        {onDownload ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="artifact-preview-download"
            onClick={onDownload}
          >
            Download
          </Button>
        ) : null}
        {downloadError ? (
          <span data-testid="artifact-preview-download-error" className="text-xs text-destructive">
            {downloadError}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}

export function ArtifactsLibraryView({
  trashed,
  items,
  selectedIds,
  loading,
  error,
  uploadError,
  uploading,
  bulkBusy,
  writeBusy,
  onToggleTrashed,
  onOpen,
  onUpload,
  onToggleSelected,
  onSelectAll,
  onClearSelected,
  onBulkTrash,
  onBulkRestore,
  onBulkEmpty,
  onMoveToTrash,
  onRestore,
  onPurge,
  onEmptyTrash,
}: ArtifactsLibraryViewProps) {
  const emptyTestId = trashed ? 'artifacts-trash-empty' : 'artifacts-empty';
  const emptyCopy = trashed
    ? 'Trash is empty.'
    : 'No artifacts yet. Upload a file to add one.';

  return (
    <PageShell
      title="Artifacts"
      description={trashed ? 'Trash — restore or empty' : 'Living library — one row per artifact'}
      headerActions={
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex">
            <span className="sr-only">Upload artifact</span>
            <input
              type="file"
              data-testid="artifacts-upload-input"
              accept={UPLOAD_ACCEPT}
              disabled={uploading}
              className="block text-sm file:mr-2 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) onUpload(file);
              }}
            />
          </label>
          {trashed ? (
            <Button
              type="button"
              variant="destructive"
              data-testid="artifacts-empty-trash"
              disabled={items.length === 0 || writeBusy || bulkBusy}
              onClick={onEmptyTrash}
            >
              Empty Trash
            </Button>
          ) : null}
        </div>
      }
      headerSlot={
        <div className="flex gap-1" role="tablist" aria-label="Library">
          <button
            type="button"
            role="tab"
            aria-selected={!trashed}
            data-testid="artifacts-tab-living"
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              !trashed
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => onToggleTrashed(false)}
          >
            Living
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={trashed}
            data-testid="artifacts-tab-trash"
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              trashed
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => onToggleTrashed(true)}
          >
            Trash
          </button>
        </div>
      }
    >
      <div data-testid="artifacts-library" data-trashed={trashed ? 'true' : 'false'}>
        {error ? (
          <div
            className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="artifacts-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        {uploadError ? (
          <div
            className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="artifacts-upload-error"
            role="alert"
          >
            {uploadError}
          </div>
        ) : null}
        {loading ? (
          <LoadingState data-testid="artifacts-loading" label="Loading artifacts…" />
        ) : null}
        {!loading && items.length === 0 ? (
          <div
            className="py-8 text-center text-sm text-muted-foreground"
            data-testid={emptyTestId}
          >
            {emptyCopy}
          </div>
        ) : null}
        {items.length > 0 ? (
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            data-testid="artifacts-bulk-bar"
          >
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="artifacts-select-all"
                checked={selectedIds.length === items.length}
                onChange={() => {
                  if (selectedIds.length === items.length) onClearSelected();
                  else onSelectAll();
                }}
              />
              Select all
            </label>
            {selectedIds.length > 0 ? (
              <>
                <span data-testid="artifacts-bulk-count" className="text-sm text-muted-foreground">
                  {selectedIds.length} selected
                </span>
                {trashed ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="artifacts-bulk-restore"
                      disabled={bulkBusy}
                      onClick={onBulkRestore}
                    >
                      Restore selected
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      data-testid="artifacts-bulk-empty"
                      disabled={bulkBusy}
                      onClick={onBulkEmpty}
                    >
                      Empty selected
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="artifacts-bulk-trash"
                    disabled={bulkBusy}
                    onClick={onBulkTrash}
                  >
                    Move selected to Trash
                  </Button>
                )}
              </>
            ) : null}
          </div>
        ) : null}
        <div data-testid="artifacts-rows" className="flex flex-col gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`artifacts-row-${item.id}`}
              data-artifact-id={item.id}
              data-selected={selectedIds.includes(item.id) ? 'true' : 'false'}
              className="flex items-center justify-between gap-3 rounded-md px-4 py-3"
            >
              <input
                type="checkbox"
                data-testid="artifacts-row-select"
                checked={selectedIds.includes(item.id)}
                onChange={() => onToggleSelected(item.id)}
                aria-label={`Select ${item.label}`}
              />
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                data-testid="artifacts-row-open"
                onClick={() => onOpen(item.id)}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" data-testid="artifacts-row-label">
                    {item.label}
                  </span>
                  <Badge variant="outline">{item.kind}</Badge>
                  <span className="text-xs text-muted-foreground" data-testid="artifacts-row-version">
                    v{item.current_version}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatCreatedAt(item.created_at)}
                </div>
              </button>
              <div className="flex shrink-0 flex-wrap gap-2">
                {trashed ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="artifacts-restore"
                      disabled={writeBusy || bulkBusy}
                      onClick={() => onRestore(item.id)}
                    >
                      Restore
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      data-testid="artifacts-purge"
                      disabled={writeBusy || bulkBusy}
                      onClick={() => onPurge(item.id)}
                    >
                      Empty
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="artifacts-move-to-trash"
                    disabled={writeBusy || bulkBusy}
                    onClick={() => onMoveToTrash(item.id)}
                  >
                    Move to Trash
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}

export function ArtifactsDetailView({
  item,
  versions,
  createdAtLabel,
  sessionHref,
  projectHref,
  projectError,
  preview,
  downloadError,
  actionError,
  onBack,
  onMoveToTrash,
  onRestoreFromTrash,
  onPurge,
  onDownload,
  onRestoreVersion,
}: ArtifactsDetailViewProps) {
  const inTrash = item.deleted_at !== undefined;

  return (
    <PageShell
      title={item.label}
      description={inTrash ? 'In Trash' : `${item.kind} · v${item.current_version}`}
      headerActions={
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" data-testid="artifacts-back" onClick={onBack}>
            Back
          </Button>
          {inTrash ? (
            <>
              {onRestoreFromTrash ? (
                <Button
                  type="button"
                  data-testid="artifacts-restore"
                  onClick={onRestoreFromTrash}
                >
                  Restore
                </Button>
              ) : null}
              {onPurge ? (
                <Button
                  type="button"
                  variant="destructive"
                  data-testid="artifacts-purge"
                  onClick={onPurge}
                >
                  Empty
                </Button>
              ) : null}
            </>
          ) : onMoveToTrash ? (
            <Button
              type="button"
              variant="outline"
              data-testid="artifacts-move-to-trash"
              onClick={onMoveToTrash}
            >
              Move to Trash
            </Button>
          ) : null}
        </div>
      }
    >
      <div data-testid="artifacts-detail" data-artifact-id={item.id}>
        {actionError ? (
          <div
            className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="artifacts-error"
            role="alert"
          >
            {actionError}
          </div>
        ) : null}

        <h2 className="sr-only">Metadata</h2>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Created</dt>
            <dd data-testid="artifacts-created-at">{createdAtLabel}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Kind</dt>
            <dd data-testid="artifacts-kind">{item.kind}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Session</dt>
            <dd>
              {item.session_id && sessionHref ? (
                <Link
                  to={sessionHref}
                  data-testid="artifacts-session-link"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {item.session_id}
                </Link>
              ) : (
                <span data-testid="artifacts-session-none">None</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Project</dt>
            <dd>
              {item.project_id && projectHref ? (
                <Link
                  to={projectHref}
                  data-testid="artifacts-project-link"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {item.project_id}
                </Link>
              ) : item.project_id ? (
                <span data-testid="artifacts-project-id">{item.project_id}</span>
              ) : (
                <span data-testid="artifacts-project-none">None</span>
              )}
              {projectError ? (
                <span
                  data-testid="artifacts-project-error"
                  className="mt-1 block text-xs text-destructive"
                  role="alert"
                >
                  {projectError}
                </span>
              ) : null}
            </dd>
          </div>
        </dl>

        <h3 className="mt-6 text-sm font-medium">Versions</h3>
        <ul data-testid="artifacts-versions" className="mt-2 flex flex-col gap-2">
          {versions.map((version) => (
            <li
              key={version.version}
              data-testid={`artifacts-version-${version.version}`}
              className="flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm"
            >
              <div>
                <span className="font-medium">v{version.version}</span>
                <span className="ml-2 text-muted-foreground">
                  {formatCreatedAt(version.created_at)} · {version.total_bytes} bytes
                </span>
                {version.warnings.length > 0 ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    warnings: {version.warnings.join(', ')}
                  </span>
                ) : null}
              </div>
              {onRestoreVersion && !inTrash && version.version !== item.current_version ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid={`artifacts-restore-version-${version.version}`}
                  onClick={() => onRestoreVersion(version.version)}
                >
                  Restore this version
                </Button>
              ) : null}
            </li>
          ))}
        </ul>

        <ArtifactPreview state={preview} downloadError={downloadError} onDownload={onDownload} />
      </div>
    </PageShell>
  );
}

function ArtifactsLibraryContainer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const trashed = searchParams.get('trashed') === '1';
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const [items, setItems] = useState<ArtifactRowModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [writeBusy, setWriteBusy] = useState(false);

  const snapshot = useMemo(
    () => ({
      route: '/artifacts',
      context: {
        surface: 'artifacts',
        primary_type: 'artifact',
        counts: [{ lane: trashed ? 'trash' : 'living', total: items.length }],
        visible: items.map((item) => ({
          type: 'artifact',
          id: item.id,
          label: item.label,
          state: trashed ? 'trashed' : 'living',
        })),
      },
    }),
    [items, trashed],
  );
  usePublishViewSnapshot(snapshot);

  useEffect(() => {
    if (!isAuthenticated || !sdkConfigured) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    void listAllMedia(trashed)
      .then((objects) => {
        if (cancelled) return;
        setItems(
          objects
            .map(rowFromObject)
            .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)),
        );
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(errorMessage(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, sdkConfigured, trashed, reloadToken]);

  const refresh = useCallback(() => {
    setSelectedIds([]);
    setReloadToken((n) => n + 1);
  }, []);

  const onUpload = useCallback(
    (file: File) => {
      setUploadError(undefined);
      let mime: string;
      try {
        mime = classifyUpload(file).mime;
      } catch (err) {
        setUploadError(errorMessage(err));
        return;
      }
      setUploading(true);
      const blob = file.type === mime ? file : new Blob([file], { type: mime });
      void getSDK()
        .media.upload(blob, { label: file.name })
        .then(() => {
          setUploading(false);
          refresh();
        })
        .catch((err: unknown) => {
          setUploading(false);
          setUploadError(errorMessage(err));
        });
    },
    [refresh],
  );

  const runAction = useCallback(
    async (work: () => Promise<void>) => {
      setError(undefined);
      setWriteBusy(true);
      try {
        await work();
        refresh();
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setWriteBusy(false);
      }
    },
    [refresh],
  );

  const runBulk = useCallback(
    async (action: 'trash' | 'undelete' | 'purge') => {
      if (selectedIds.length === 0) {
        throw new Error('bulk action requires at least one selected artifact');
      }
      setError(undefined);
      setBulkBusy(true);
      try {
        await getSDK().media.bulk({ action, ids: selectedIds });
        refresh();
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBulkBusy(false);
      }
    },
    [refresh, selectedIds],
  );

  return (
    <div data-testid="artifacts-view">
      <ArtifactsLibraryView
        trashed={trashed}
        items={items}
        selectedIds={selectedIds}
        loading={loading}
        error={error}
        uploadError={uploadError}
        uploading={uploading}
        bulkBusy={bulkBusy}
        writeBusy={writeBusy}
        onToggleTrashed={(next) => {
          setSelectedIds([]);
          if (next) setSearchParams({ trashed: '1' });
          else setSearchParams({});
        }}
        onOpen={(id) => {
          navigate(trashed ? `/artifacts/${id}?trashed=1` : `/artifacts/${id}`);
        }}
        onUpload={onUpload}
        onToggleSelected={(id) => {
          setSelectedIds((current) =>
            current.includes(id) ? current.filter((row) => row !== id) : [...current, id],
          );
        }}
        onSelectAll={() => {
          setSelectedIds(items.map((item) => item.id));
        }}
        onClearSelected={() => {
          setSelectedIds([]);
        }}
        onBulkTrash={() => {
          void runBulk('trash');
        }}
        onBulkRestore={() => {
          void runBulk('undelete');
        }}
        onBulkEmpty={() => {
          void runBulk('purge');
        }}
        onMoveToTrash={(id) => {
          void runAction(() => getSDK().media.delete(id));
        }}
        onRestore={(id) => {
          void runAction(async () => {
            await getSDK().media.undelete(id);
          });
        }}
        onPurge={(id) => {
          void runAction(() => getSDK().media.purge(id));
        }}
        onEmptyTrash={() => {
          void runAction(() => getSDK().media.emptyTrash());
        }}
      />
    </div>
  );
}

function ArtifactsDetailContainer({ id }: { id: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const [item, setItem] = useState<ArtifactRowModel | undefined>(undefined);
  const [versions, setVersions] = useState<ArtifactVersionRow[]>([]);
  const [preview, setPreview] = useState<ArtifactPreviewState>({ kind: 'loading' });
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [sessionHref, setSessionHref] = useState<string | undefined>(undefined);
  const [projectHref, setProjectHref] = useState<string | undefined>(undefined);
  const [projectError, setProjectError] = useState<string | undefined>(undefined);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [reloadToken, setReloadToken] = useState(0);

  const snapshot = useMemo(
    () =>
      item
        ? {
            route: `/artifacts/${id}`,
            context: {
              surface: 'artifacts',
              primary_type: 'artifact',
              counts: [{ lane: 'detail', total: 1 }],
              visible: [{ type: 'artifact', id: item.id, label: item.label }],
            },
          }
        : null,
    [id, item],
  );
  usePublishViewSnapshot(snapshot);

  useEffect(() => {
    if (!isAuthenticated || !sdkConfigured) return;
    let cancelled = false;
    let objectUrl: string | undefined;
    setLoadError(undefined);
    setPreview({ kind: 'loading' });

    const load = async () => {
      const sdk = getSDK();
      const object = await sdk.media.get(id);
      if (cancelled) return;
      setItem(rowFromObject(object));
      if (!object.versions) {
        throw new Error(`media ${object.id} get omitted versions`);
      }
      const versionRows = object.versions.map((row) => ({
        version: row.version,
        created_at: row.created_at,
        warnings: row.warnings,
        total_bytes: row.total_bytes,
      }));
      setVersions(versionRows);
      setSessionHref(object.session_id ? `/chat/${object.session_id}` : undefined);
      setProjectHref(undefined);
      setProjectError(undefined);
      if (object.project_id) {
        try {
          const detail = await sdk.workManagement.getProject(object.project_id);
          const initiative = await sdk.workManagement.getInitiative(detail.project.initiative_id);
          if (!cancelled) {
            setProjectHref(
              `/work/teams/${detail.project.team_id}/goals/${initiative.goal_id}/projects/${object.project_id}`,
            );
          }
        } catch (err) {
          if (!cancelled) setProjectError(errorMessage(err));
        }
      }

      if (object.deleted_at) {
        setPreview({ kind: 'tombstone' });
        return;
      }

      const version = currentVersion(object);
      const warned = version.warnings.length > 0;
      if (!version.formats) {
        throw new Error(`media ${object.id} v${version.version} omitted formats`);
      }
      const formats = version.formats.map((row) => row.format);
      const isHtml = formats.includes('html');
      const isPdf = formats.includes('pdf');
      const raster = formats.includes('png');
      const vector = formats.includes('svg');
      const jpeg = formats.includes('jpeg');
      const webp = formats.includes('webp');

      if (isHtml && !raster) {
        setPreview({ kind: 'download-only', label: object.label, warnings: version.warnings });
        return;
      }
      if (isPdf && !raster && !vector && !jpeg && !webp) {
        setPreview({ kind: 'download-only', label: object.label, warnings: version.warnings });
        return;
      }

      const format = warned && raster ? 'png' : vector ? 'svg' : raster ? 'png' : jpeg ? 'jpeg' : webp ? 'webp' : undefined;
      if (!format) {
        setPreview({ kind: 'download-only', label: object.label, warnings: version.warnings });
        return;
      }

      const width = version.width;
      const height = version.height;
      if (width !== undefined && height !== undefined) {
        setPreview({ kind: 'loading', width, height });
      }
      const raw = await sdk.media.content(id, { format, version: version.version });
      if (cancelled) return;
      const bytes = format === 'svg' ? ensureStandaloneSvg(raw) : raw;
      const blob = new Blob([bytes], { type: previewMime(format) });
      objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      if (width !== undefined && height !== undefined) {
        setPreview({
          kind: 'ready',
          width,
          height,
          previewUrl: objectUrl,
          previewKind: format === 'svg' ? 'svg' : 'png',
          warnings: version.warnings,
          label: object.label,
        });
        return;
      }
      setPreview({
        kind: 'ready-unmeasured',
        previewUrl: objectUrl,
        previewKind: format === 'svg' ? 'svg' : 'png',
        warnings: version.warnings,
        label: object.label,
      });
    };

    void load().catch((err: unknown) => {
      if (cancelled) return;
      setLoadError(errorMessage(err));
      setPreview({ kind: 'failed', message: errorMessage(err) });
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, isAuthenticated, sdkConfigured, reloadToken]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const backTo = searchParams.get('trashed') === '1' || item?.deleted_at ? '/artifacts?trashed=1' : '/artifacts';

  const runAction = useCallback(
    async (work: () => Promise<void>, after?: () => void) => {
      setActionError(undefined);
      try {
        await work();
        if (after) after();
        else refresh();
      } catch (err) {
        setActionError(errorMessage(err));
      }
    },
    [refresh],
  );

  const onDownload = useCallback(() => {
    if (!item) return;
    setDownloadError(undefined);
    void getSDK()
      .media.content(item.id)
      .then((bytes) => {
        triggerDownload(bytes, filenameFor(item.label, 'latest'), 'application/octet-stream');
      })
      .catch((err: unknown) => {
        setDownloadError(errorMessage(err));
      });
  }, [item]);

  if (loadError && !item) {
    return (
      <PageShell title="Artifacts" headerActions={
        <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
          Back
        </Button>
      }>
        <div data-testid="artifacts-error" role="alert" className="text-sm text-destructive">
          {loadError}
        </div>
      </PageShell>
    );
  }

  if (!item) {
    return (
      <PageShell title="Artifacts">
        <LoadingState data-testid="artifacts-loading" label="Loading artifact…" />
      </PageShell>
    );
  }

  return (
    <div data-testid="artifacts-view">
      <ArtifactsDetailView
        item={item}
        versions={versions}
        createdAtLabel={formatCreatedAt(item.created_at)}
        sessionHref={sessionHref}
        projectHref={projectHref}
        projectError={projectError}
        preview={preview}
        downloadError={downloadError}
        actionError={actionError}
        onBack={() => navigate(backTo)}
        onMoveToTrash={
          item.deleted_at
            ? undefined
            : () => {
                void runAction(() => getSDK().media.delete(id), () => navigate('/artifacts?trashed=1'));
              }
        }
        onRestoreFromTrash={
          item.deleted_at
            ? () => {
                void runAction(
                  async () => {
                    await getSDK().media.undelete(id);
                  },
                  () => navigate(`/artifacts/${id}`),
                );
              }
            : undefined
        }
        onPurge={
          item.deleted_at
            ? () => {
                void runAction(() => getSDK().media.purge(id), () => navigate('/artifacts?trashed=1'));
              }
            : undefined
        }
        onDownload={onDownload}
        onRestoreVersion={(version) => {
          void runAction(async () => {
            await getSDK().media.restore(id, version);
          });
        }}
      />
    </div>
  );
}

export function ArtifactsView() {
  const { id } = useParams<{ id?: string }>();
  if (id) return <ArtifactsDetailContainer id={id} />;
  return <ArtifactsLibraryContainer />;
}
